import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Skeleton from "@mui/material/Skeleton";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import {
  AttachMoney,
  CalendarMonth,
  Cancel,
  Download,
  People,
  Search,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import RechartsChartTooltip from "@/components/RechartsChartTooltip";
import { formatCents, getStatusChipColor } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n/i18n";

interface BookingRow {
  ref: string;
  date: string;
  time: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  service: string;
  staff: string;
  status: string;
  priceCents: number;
  depositCents: number;
  currency: string;
  source: string;
  notes: string;
}

interface ReportData {
  currency: string;
  totalRevenue: number;
  totalBookings: number;
  cancellationRate: number;
  bookingsByStatus: Record<string, number>;
  revenueByService: { name: string; count: number; revenue: number }[];
  bookingsByStaff: { name: string; count: number; revenue: number }[];
  bookingsByDay: { date: string; count: number; revenue: number }[];
  bookings: BookingRow[];
}

/** API may send a service name string or a nested service object. */
function offeringLabel(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "name" in value && typeof (value as { name: unknown }).name === "string") {
    return (value as { name: string }).name;
  }
  return "";
}

/** API may send a staff name string or a nested staff object. */
function staffLabel(value: unknown): string {
  if (typeof value === "string") return value;
  if (
    value &&
    typeof value === "object" &&
    "displayName" in value &&
    typeof (value as { displayName: unknown }).displayName === "string"
  ) {
    return (value as { displayName: string }).displayName;
  }
  return "";
}

function serviceLabelFromRow(b: Record<string, unknown>): string {
  const named = offeringLabel(b.service);
  if (named) return named;
  if (typeof b.serviceId === "number") return i18n.t("reports.offeringById", { id: b.serviceId });
  return "";
}

function staffLabelFromRow(b: Record<string, unknown>): string {
  const named = staffLabel(b.staff ?? b.staffMember);
  if (named) return named;
  if (typeof b.staffId === "number") return i18n.t("reports.staffById", { id: b.staffId });
  return "";
}

/** API may return CONFIRMED or confirmed; normalize for filters and chips. */
function normalizeReportStatus(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.toLowerCase();
}

function aggregateRevenueByService(rows: unknown[]): { name: string; count: number; revenue: number }[] {
  const map = new Map<string, { name: string; count: number; revenue: number }>();
  for (const raw of rows) {
    if (!raw || typeof raw !== "object") continue;
    const b = raw as Record<string, unknown>;
    const sid = b.serviceId;
    const key = typeof sid === "number" ? `s:${sid}` : "unknown";
    const name = serviceLabelFromRow(b);
    const priceCents = typeof b.priceCents === "number" ? b.priceCents : Number(b.priceCents) || 0;
    const prev = map.get(key);
    if (prev) {
      prev.count += 1;
      prev.revenue += priceCents;
    } else {
      map.set(key, { name: name || key, count: 1, revenue: priceCents });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

function aggregateBookingsByStaff(rows: unknown[]): { name: string; count: number; revenue: number }[] {
  const map = new Map<string, { name: string; count: number; revenue: number }>();
  for (const raw of rows) {
    if (!raw || typeof raw !== "object") continue;
    const b = raw as Record<string, unknown>;
    const sid = b.staffId;
    const key = typeof sid === "number" ? `st:${sid}` : "unknown";
    const name = staffLabelFromRow(b);
    const priceCents = typeof b.priceCents === "number" ? b.priceCents : Number(b.priceCents) || 0;
    const prev = map.get(key);
    if (prev) {
      prev.count += 1;
      prev.revenue += priceCents;
    } else {
      map.set(key, { name: name || key, count: 1, revenue: priceCents });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.revenue - a.revenue);
}

function aggregateBookingsByDay(rows: unknown[]): { date: string; count: number; revenue: number }[] {
  const map = new Map<string, { count: number; revenue: number }>();
  for (const raw of rows) {
    if (!raw || typeof raw !== "object") continue;
    const b = raw as Record<string, unknown>;
    const startAt = typeof b.startAt === "string" ? b.startAt : "";
    if (!startAt) continue;
    const day = startAt.slice(0, 10);
    const priceCents = typeof b.priceCents === "number" ? b.priceCents : Number(b.priceCents) || 0;
    const prev = map.get(day);
    if (prev) {
      prev.count += 1;
      prev.revenue += priceCents;
    } else {
      map.set(day, { count: 1, revenue: priceCents });
    }
  }
  return Array.from(map.entries())
    .map(([date, v]) => ({ date, ...v }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Maps both flat report rows and full `BookingWithDetails`-like payloads to `BookingRow`. */
function normalizeBookingRow(raw: unknown): BookingRow {
  if (!raw || typeof raw !== "object") {
    return {
      ref: "",
      date: "",
      time: "",
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      service: "",
      staff: "",
      status: "",
      priceCents: 0,
      depositCents: 0,
      currency: "GBP",
      source: "",
      notes: "",
    };
  }
  const b = raw as Record<string, unknown>;
  const service = serviceLabelFromRow(b);
  const staff = staffLabelFromRow(b);

  let customerName = "";
  let customerEmail = "";
  let customerPhone = "";
  const cust = b.customer;
  if (cust && typeof cust === "object") {
    const c = cust as Record<string, unknown>;
    customerName = `${typeof c.firstName === "string" ? c.firstName : ""} ${typeof c.lastName === "string" ? c.lastName : ""}`.trim();
    customerEmail = typeof c.email === "string" ? c.email : "";
    customerPhone = typeof c.phone === "string" ? c.phone : "";
  } else {
    customerName = typeof b.customerName === "string" ? b.customerName : "";
    customerEmail = typeof b.customerEmail === "string" ? b.customerEmail : "";
    customerPhone = typeof b.customerPhone === "string" ? b.customerPhone : "";
  }

  const startAt = typeof b.startAt === "string" ? b.startAt : "";
  const start = startAt ? new Date(startAt) : new Date();
  const date =
    typeof b.date === "string"
      ? b.date
      : start.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const time =
    typeof b.time === "string"
      ? b.time
      : `${String(start.getHours()).padStart(2, "0")}:${String(start.getMinutes()).padStart(2, "0")}`;

  const ref =
    typeof b.ref === "string"
      ? b.ref
      : typeof b.bookingId === "string"
        ? b.bookingId
        : typeof b.id === "string"
          ? b.id
          : typeof b.manageToken === "string"
            ? b.manageToken
            : "";

  const priceCents = typeof b.priceCents === "number" ? b.priceCents : Number(b.priceCents) || 0;
  const depositRaw = b.depositCents ?? b.depositAmountCents;
  const depositCents = typeof depositRaw === "number" ? depositRaw : Number(depositRaw) || 0;

  return {
    ref,
    date,
    time,
    customerName,
    customerEmail,
    customerPhone,
    service,
    staff,
    status: normalizeReportStatus(b.status),
    priceCents,
    depositCents,
    currency: typeof b.currency === "string" ? b.currency : "GBP",
    source: typeof b.source === "string" ? b.source : "",
    notes: typeof b.notes === "string" ? b.notes : "",
  };
}

/**
 * Builds OpenAPI `from` / `to` for GET admin reports (`to` is exclusive per spec).
 * Range covers the last N calendar days ending today.
 */
function adminReportsRangeFromDays(daysStr: string): { from: string; to: string } {
  const n = Math.min(365, Math.max(1, parseInt(daysStr, 10) || 30));
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  to.setDate(to.getDate() + 1);
  const from = new Date(to);
  from.setDate(from.getDate() - n);
  return { from: from.toISOString(), to: to.toISOString() };
}

function normalizeReportPayload(raw: unknown): ReportData {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid reports payload");
  }
  const r = raw as Record<string, unknown>;

  /** Real API: `{ data, metadata, summary }`; legacy mock: `{ bookings, totalRevenue, … }`. */
  const rowsFromApi = Array.isArray(r.data) ? r.data : null;
  const bookingsRaw = rowsFromApi ?? (Array.isArray(r.bookings) ? r.bookings : []);
  const bookings = bookingsRaw.map(normalizeBookingRow);

  const summary = r.summary && typeof r.summary === "object" ? (r.summary as Record<string, unknown>) : null;

  let currency = typeof r.currency === "string" ? r.currency : "GBP";
  if (rowsFromApi?.length && typeof r.currency !== "string") {
    const first = rowsFromApi[0];
    if (first && typeof first === "object" && typeof (first as Record<string, unknown>).currency === "string") {
      currency = (first as Record<string, unknown>).currency as string;
    }
  }

  const revenueByServiceFromRoot = Array.isArray(r.revenueByService)
    ? (r.revenueByService as unknown[]).map((item) => {
        const x = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
        return {
          name: offeringLabel(x.name ?? x.service),
          count: typeof x.count === "number" ? x.count : Number(x.count) || 0,
          revenue: typeof x.revenue === "number" ? x.revenue : Number(x.revenue) || 0,
        };
      })
    : [];

  const bookingsByStaffFromRoot = Array.isArray(r.bookingsByStaff)
    ? (r.bookingsByStaff as unknown[]).map((item) => {
        const x = item && typeof item === "object" ? (item as Record<string, unknown>) : {};
        return {
          name: typeof x.name === "string" ? x.name : staffLabel(x.name ?? x.staff),
          count: typeof x.count === "number" ? x.count : Number(x.count) || 0,
          revenue: typeof x.revenue === "number" ? x.revenue : Number(x.revenue) || 0,
        };
      })
    : [];

  const bookingsByDayFromRoot = Array.isArray(r.bookingsByDay)
    ? (r.bookingsByDay as { date: string; count: number; revenue: number }[])
    : [];

  let totalBookings: number;
  let totalRevenue: number;
  let cancellationRate: number;
  let bookingsByStatus: Record<string, number>;

  if (summary) {
    totalBookings = typeof summary.totalBookings === "number" ? summary.totalBookings : Number(summary.totalBookings) || 0;
    totalRevenue =
      typeof summary.totalRevenueCents === "number"
        ? summary.totalRevenueCents
        : typeof summary.totalRevenue === "number"
          ? summary.totalRevenue
          : Number(summary.totalRevenueCents ?? summary.totalRevenue) || 0;

    const confirmed =
      typeof summary.confirmedCount === "number" ? summary.confirmedCount : Number(summary.confirmedCount) || 0;
    const cancelled =
      typeof summary.cancelledCount === "number" ? summary.cancelledCount : Number(summary.cancelledCount) || 0;
    const completed =
      typeof summary.completedCount === "number" ? summary.completedCount : Number(summary.completedCount) || 0;
    const pendingPayment =
      typeof summary.pendingPaymentCount === "number"
        ? summary.pendingPaymentCount
        : Number(summary.pendingPaymentCount) || 0;
    const noShow =
      typeof summary.noShowCount === "number" ? summary.noShowCount : Number(summary.noShowCount) || 0;

    bookingsByStatus = {
      confirmed,
      cancelled,
      completed,
      pending_payment: pendingPayment,
      no_show: noShow,
    };

    cancellationRate =
      totalBookings > 0 ? Math.round((cancelled / totalBookings) * 10000) / 100 : 0;
  } else if (rowsFromApi) {
    totalBookings = bookingsRaw.length;
    totalRevenue = bookingsRaw.reduce((acc, row) => {
      if (!row || typeof row !== "object") return acc;
      const p = (row as Record<string, unknown>).priceCents;
      const n = typeof p === "number" ? p : Number(p) || 0;
      return acc + n;
    }, 0);
    const tallies: Record<string, number> = {
      confirmed: 0,
      cancelled: 0,
      completed: 0,
      pending_payment: 0,
      no_show: 0,
    };
    for (const row of bookingsRaw) {
      if (!row || typeof row !== "object") continue;
      const st = normalizeReportStatus((row as Record<string, unknown>).status);
      if (st in tallies) tallies[st] += 1;
    }
    bookingsByStatus = tallies;
    const {cancelled} = tallies;
    cancellationRate =
      totalBookings > 0 ? Math.round((cancelled / totalBookings) * 10000) / 100 : 0;
  } else {
    totalBookings = typeof r.totalBookings === "number" ? r.totalBookings : Number(r.totalBookings) || 0;
    totalRevenue = typeof r.totalRevenue === "number" ? r.totalRevenue : Number(r.totalRevenue) || 0;
    cancellationRate =
      typeof r.cancellationRate === "number" ? r.cancellationRate : Number(r.cancellationRate) || 0;
    bookingsByStatus =
      r.bookingsByStatus && typeof r.bookingsByStatus === "object"
        ? (r.bookingsByStatus as Record<string, number>)
        : {};
  }

  const revenueByService =
    revenueByServiceFromRoot.length > 0 ? revenueByServiceFromRoot : aggregateRevenueByService(bookingsRaw);
  const bookingsByStaff =
    bookingsByStaffFromRoot.length > 0 ? bookingsByStaffFromRoot : aggregateBookingsByStaff(bookingsRaw);
  const bookingsByDay =
    bookingsByDayFromRoot.length > 0 ? bookingsByDayFromRoot : aggregateBookingsByDay(bookingsRaw);

  return {
    currency,
    totalRevenue,
    totalBookings,
    cancellationRate,
    bookingsByStatus,
    revenueByService,
    bookingsByStaff,
    bookingsByDay,
    bookings,
  };
}

const STATUS_HEX: Record<string, string> = {
  confirmed: "#22c55e",
  completed: "#3b82f6",
  cancelled: "#ef4444",
  no_show: "#f59e0b",
  pending_payment: "#a855f7",
};

const PIE_FALLBACK = ["#7c3aed", "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#ec4899", "#14b8a6", "#f97316"];

function shortDate(dateStr: string, days: number): string {
  const d = new Date(dateStr);
  if (days <= 7) return d.toLocaleDateString("en-GB", { weekday: "short" });
  if (days <= 31) return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** YYYY-MM-DD for matching API `bookingsByDay` rows. */
function normalizeDayKey(raw: string): string {
  if (!raw || typeof raw !== "string") return "";
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return raw.slice(0, 10);
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * One point per calendar day for the selected period (aligned with dashboard “last N days” logic).
 * Missing days get count/revenue 0 so the x-axis spans the full range instead of a single API row.
 */
function buildDenseBookingsByDaySeries(
  periodDays: number,
  sparse: { date: string; count: number; revenue: number }[],
): { date: string; count: number; revenue: number; label: string }[] {
  const map = new Map<string, { count: number; revenue: number }>();
  for (const row of sparse) {
    const key = normalizeDayKey(row.date);
    if (!key) continue;
    map.set(key, {
      count: typeof row.count === "number" ? row.count : Number(row.count) || 0,
      revenue: typeof row.revenue === "number" ? row.revenue : Number(row.revenue) || 0,
    });
  }
  const end = new Date();
  end.setHours(12, 0, 0, 0);
  const result: { date: string; count: number; revenue: number; label: string }[] = [];
  for (let i = periodDays - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    const merged = map.get(iso);
    result.push({
      date: iso,
      count: merged?.count ?? 0,
      revenue: merged?.revenue ?? 0,
      label: shortDate(iso, periodDays),
    });
  }
  return result;
}

function StatCard({
  title,
  value,
  sub,
  icon: Icon,
}: {
  title: string;
  value: string;
  sub?: string;
  icon: typeof AttachMoney;
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
            <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon color="primary" fontSize="small" />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AdminReports() {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [days, setDays] = useState("30");
  const [extractSearch, setExtractSearch] = useState("");
  const [extractStatus, setExtractStatus] = useState("all");

  const { data, isLoading } = useQuery<ReportData>({
    queryKey: ["/api/admin/reports", days],
    queryFn: async () => {
      const { from, to } = adminReportsRangeFromDays(days);
      const qs = new URLSearchParams({
        from,
        to,
      });
      const res = await apiRequest("GET", `/api/admin/reports?${qs.toString()}`);
      const json = await res.json();
      return normalizeReportPayload(json);
    },
  });

  const displayData = useMemo((): ReportData | undefined => {
    if (isLoading) return undefined;
    return data;
  }, [data, isLoading]);

  const currency = displayData?.currency || "GBP";

  const pieData = displayData
    ? Object.entries(displayData.bookingsByStatus).map(([status, count]) => ({ name: status, value: count }))
    : [];

  const chartDays = Math.min(365, Math.max(1, parseInt(days, 10) || 30));
  const dayLabels = useMemo(() => {
    if (!displayData) return [];
    return buildDenseBookingsByDaySeries(chartDays, displayData.bookingsByDay || []);
  }, [displayData, chartDays]);

  const filteredBookings = useMemo(() => (displayData?.bookings || []).filter((b) => {
      if (extractStatus !== "all" && b.status !== extractStatus) return false;
      if (extractSearch) {
        const q = extractSearch.toLowerCase();
        if (
          !b.customerName.toLowerCase().includes(q) &&
          !b.customerEmail.toLowerCase().includes(q) &&
          !b.service.toLowerCase().includes(q) &&
          !b.ref.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    }), [displayData?.bookings, extractSearch, extractStatus]);

  function exportCsv() {
    const headers = [
      t("reports.csvColumnRef"),
      t("common.date"),
      t("common.time"),
      t("reports.table.customer"),
      t("reports.csvColumnEmail"),
      t("reports.csvColumnPhone"),
      t("reports.table.offering"),
      t("reports.table.staff"),
      t("reports.table.status"),
      t("reports.table.price"),
      t("reports.csvColumnDeposit"),
      t("reports.csvColumnCurrency"),
      t("reports.csvColumnSource"),
      t("reports.table.notes"),
    ];
    const rows = filteredBookings.map((b) => [
      b.ref,
      b.date,
      b.time,
      b.customerName,
      b.customerEmail,
      b.customerPhone,
      b.service,
      b.staff,
      b.status,
      (b.priceCents / 100).toFixed(2),
      (b.depositCents / 100).toFixed(2),
      b.currency,
      b.source,
      b.notes,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const period = days === "365" ? "12months" : `${days}days`;
    a.download = `bookings-export-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const periodLabel = t(`reports.periodLabels.${days}` as "reports.periodLabels.30");

  const tickInterval = chartDays <= 7 ? 0 : chartDays <= 31 ? 3 : 14;

  const yAxisMoney = (v: number) =>
    `${currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$"}${(v / 100).toFixed(0)}`;

  const axisTick = useMemo(() => ({ fontSize: 11, fill: theme.palette.text.secondary }), [theme]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} data-testid="text-reports-title">
            {t("reports.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("reports.subtitle")}
          </Typography>
        </Box>
        <TextField
          select
          size="small"
          label={t("reports.period")}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          sx={{ minWidth: 180 }}
          data-testid="select-reports-period"
        >
          <MenuItem value="7">{t("reports.period7")}</MenuItem>
          <MenuItem value="30">{t("reports.period30")}</MenuItem>
          <MenuItem value="90">{t("reports.period90")}</MenuItem>
          <MenuItem value="365">{t("reports.period365")}</MenuItem>
        </TextField>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={112} />
          ))}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <StatCard
              title={t("reports.totalRevenue")}
              value={formatCents(displayData?.totalRevenue || 0, currency)}
              sub={t("reports.totalRevenueSub")}
              icon={AttachMoney}
            />
            <StatCard
              title={t("reports.totalBookings")}
              value={String(displayData?.totalBookings || 0)}
              sub={t("reports.totalBookingsSub", { days })}
              icon={CalendarMonth}
            />
            <StatCard
              title={t("reports.cancellationRate")}
              value={`${displayData?.cancellationRate || 0}%`}
              sub={t("reports.cancellationSub", { count: displayData?.bookingsByStatus?.cancelled || 0 })}
              icon={Cancel}
            />
            <StatCard
              title={t("reports.staffInPeriod")}
              value={String(displayData?.bookingsByStaff?.length || 0)}
              sub={t("reports.staffInPeriodSub")}
              icon={People}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Card variant="outlined">
              <CardHeader title={t("reports.bookingsOverTime")} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={dayLabels}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="label" tick={axisTick} interval={tickInterval} />
                    <YAxis tick={axisTick} allowDecimals={false} />
                    <Tooltip
                      content={RechartsChartTooltip}
                      formatter={(v: number) => [v, t("reports.chartBookings")]}
                    />
                    <Line type="monotone" dataKey="count" stroke={primary} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader title={t("reports.revenueOverTime")} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dayLabels}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="label" tick={axisTick} interval={tickInterval} />
                    <YAxis tick={axisTick} tickFormatter={yAxisMoney} />
                    <Tooltip
                      content={RechartsChartTooltip}
                      formatter={(v: number) => [formatCents(v, currency), t("reports.chartRevenue")]}
                    />
                    <Bar dataKey="revenue" fill={primary} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 2fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <Card variant="outlined">
              <CardHeader title={t("reports.bookingsByStatus")} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
              <CardContent>
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell
                            key={entry.name}
                            fill={STATUS_HEX[entry.name] || PIE_FALLBACK[index % PIE_FALLBACK.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={RechartsChartTooltip} />
                      <Legend
                        formatter={(v) => <span style={{ fontSize: 12, color: theme.palette.text.primary }}>{String(v).replace("_", " ")}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography color="text.secondary">{t("common.noData")}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardHeader title={t("reports.topOfferings")} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
              <CardContent>
                {(displayData?.revenueByService?.length || 0) > 0 ? (
                  <Stack spacing={1.5}>
                    {displayData!.revenueByService.map((svc, i) => {
                      const maxCount = Math.max(...displayData!.revenueByService.map((s) => s.count), 1);
                      const pct = Math.round((svc.count / maxCount) * 100);
                      return (
                        <Box key={i}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: "60%" }}>
                              {svc.name}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                              <Chip size="small" label={t("reports.bookingsCount", { count: svc.count })} />
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: "right" }}>
                                {formatCents(svc.revenue, currency)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ height: 6, bgcolor: "action.hover", borderRadius: 999 }}>
                            <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: "primary.main", borderRadius: 999 }} />
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography color="text.secondary">{t("common.noData")}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardHeader title={t("reports.staffPerformance")} titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }} />
            <CardContent>
              {(displayData?.bookingsByStaff?.length || 0) > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={displayData!.bookingsByStaff} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} horizontal={false} />
                    <XAxis type="number" tick={axisTick} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={axisTick} width={100} />
                    <Tooltip
                      content={RechartsChartTooltip}
                      formatter={(v: number, name: string) =>
                        name === "revenue"
                          ? [formatCents(v, currency), t("reports.chartRevenue")]
                          : [v, t("reports.chartBookings")]
                      }
                    />
                    <Bar dataKey="count" name={t("reports.chartBookings")} fill={primary} radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary">{t("common.noDataForPeriod")}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader
              title={t("reports.extractTitle")}
              subheader={t("reports.extractSub", {
                count: filteredBookings.length,
                period: periodLabel,
              })}
              titleTypographyProps={{ variant: "subtitle1", fontWeight: 600 }}
              action={
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Download />}
                  onClick={exportCsv}
                  disabled={filteredBookings.length === 0}
                  data-testid="button-export-report-csv"
                >
                  {t("reports.exportCsv")}
                </Button>
              }
            />
            <CardContent sx={{ pt: 0 }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                <TextField
                  size="small"
                  placeholder={t("reports.searchPlaceholder")}
                  value={extractSearch}
                  onChange={(e) => setExtractSearch(e.target.value)}
                  sx={{ flex: 1, minWidth: 260 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  data-testid="input-extract-search"
                />
                <TextField
                  select
                  size="small"
                  label={t("reports.statusFilter")}
                  value={extractStatus}
                  onChange={(e) => setExtractStatus(e.target.value)}
                  sx={{ minWidth: 160 }}
                  data-testid="select-extract-status"
                >
                  <MenuItem value="all">{t("reports.allStatuses")}</MenuItem>
                  <MenuItem value="confirmed">{t("reports.confirmed")}</MenuItem>
                  <MenuItem value="completed">{t("reports.completed")}</MenuItem>
                  <MenuItem value="cancelled">{t("reports.cancelled")}</MenuItem>
                  <MenuItem value="pending_payment">{t("reports.pendingPayment")}</MenuItem>
                  <MenuItem value="no_show">{t("reports.noShow")}</MenuItem>
                </TextField>
              </Box>
              {filteredBookings.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                  {t("reports.noMatchFilters")}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("reports.table.ref")}</TableCell>
                      <TableCell>{t("reports.table.date")}</TableCell>
                      <TableCell>{t("reports.table.customer")}</TableCell>
                      <TableCell>{t("reports.table.offering")}</TableCell>
                      <TableCell>{t("reports.table.staff")}</TableCell>
                      <TableCell>{t("reports.table.status")}</TableCell>
                      <TableCell align="right">{t("reports.table.price")}</TableCell>
                      <TableCell>{t("reports.table.notes")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredBookings.map((b) => (
                      <TableRow key={b.ref} hover data-testid={`row-extract-${b.ref}`}>
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace" color="text.secondary">
                            {b.ref}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="caption" component="span">
                            {b.date}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>
                            {b.time}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {b.customerName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {b.customerEmail}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{b.service}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {b.staff}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={b.status.replace("_", " ")}
                            color={getStatusChipColor(b.status)}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCents(b.priceCents, b.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="caption" color="text.secondary" noWrap title={b.notes}>
                            {b.notes || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
